import { put, all, call, takeLatest } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { success, error } from 'react-toastify-redux';

import {
  runAssessment as runAssessmentApi,
  getAssessments as getAssessmentsApi,
  getTopFeatures as getTopFeaturesApi,
  getHigherEdSurveys as getSurveysApi,
  createHigherEdSurvey as createSurveyApi,
  getHigherEdSurveyDetails as getSurveyDetailsApi,
  answerHigherEdSurvey as answerSurveyApi,
  removeHigherEdSurvey as removeSurveyApi,
} from '../services';

import actions from './actions';
import types from './types';

const draftKey = projectId => `higherEd_draft_${projectId}`;

function* runAssessmentProcess(action) {
  try {
    const { projectId, payload } = action.payload;
    const response = yield call(runAssessmentApi, projectId, payload);
    if (response && response.error) {
      yield put(actions.runAssessmentFailure(response.error.message || 'Assessment failed'));
      yield put(error('Higher Ed assessment failed'));
      return;
    }
    yield put(actions.runAssessmentSuccess(response));
    yield put(success('Higher Ed assessment computed'));
    // Refresh history list + top features so dashboard reflects the new record.
    yield put(actions.getAssessments(projectId));
    yield put(actions.getTopFeatures(projectId, 5));
  } catch (err) {
    yield put(actions.runAssessmentFailure(err.message || String(err)));
    yield put(error('Higher Ed assessment failed'));
  }
}

function* getAssessmentsProcess(action) {
  try {
    const projectId = action.payload;
    const response = yield call(getAssessmentsApi, projectId);
    const list = Array.isArray(response) ? response : [];
    yield put(actions.getAssessmentsSuccess(list));
    // If we have a latest assessment but no draft loaded yet, auto-populate
    // the assessment slot so the dashboard renders immediately.
    if (list.length > 0) {
      yield put(actions.runAssessmentSuccess(list[0]));
      // Eagerly load top features so the PDF download button is ready when clicked.
      yield put(actions.getTopFeatures(projectId, 5));
    }
  } catch (err) {
    yield put(actions.getAssessmentsFailure(err.message || String(err)));
  }
}

function* getTopFeaturesProcess(action) {
  try {
    const { projectId, topN } = action.payload;
    const response = yield call(getTopFeaturesApi, projectId, topN);
    yield put(actions.getTopFeaturesSuccess(Array.isArray(response) ? response : []));
  } catch (err) {
    yield put(actions.getTopFeaturesFailure(err.message || String(err)));
  }
}

function* hydrateDraftProcess(action) {
  try {
    const projectId = action.payload;
    const raw = localStorage.getItem(draftKey(projectId));
    if (raw) {
      const draft = JSON.parse(raw);
      yield put(actions.setDraft(projectId, draft));
    }
  } catch (err) {
    // ignore — saga keeps defaults
  }
}

function* setDraftProcess(action) {
  try {
    const { projectId, draft } = action.payload;
    localStorage.setItem(draftKey(projectId), JSON.stringify(draft));
  } catch (err) {
    // localStorage may be unavailable in some browsers/contexts; ignore
  }
}

// ─────────────────────────────────────────────────────────────
// Higher Ed surveys -- phase 2 (2026-06-28)
// ─────────────────────────────────────────────────────────────

function* getSurveysProcess(action) {
  try {
    const projectId = action.payload;
    const response = yield call(getSurveysApi, projectId);
    yield put(actions.getSurveysSuccess(Array.isArray(response) ? response : []));
  } catch (err) {
    yield put(actions.getSurveysFailure(err.message || String(err)));
  }
}

function* createSurveyProcess(action) {
  try {
    const { projectId, surveyName } = action.payload;
    const response = yield call(createSurveyApi, projectId, surveyName);
    yield put(actions.createSurveySuccess(response));
    yield put(success('Survey created'));
    yield put(push(`/projects/${projectId}/higher-ed/surveys/${response.id}/created`));
  } catch (err) {
    yield put(actions.createSurveyFailure(err.message || String(err)));
    yield put(error(`Failed to create survey: ${err.message || err}`));
  }
}

function* getSurveyDetailsProcess(action) {
  try {
    const surveyId = action.payload;
    const response = yield call(getSurveyDetailsApi, surveyId);
    yield put(actions.getSurveyDetailsSuccess(response));
  } catch (err) {
    yield put(actions.getSurveyDetailsFailure(err.message || String(err)));
  }
}

function* answerSurveyProcess(action) {
  try {
    const { surveyId, capabilitiesValues } = action.payload;
    yield call(answerSurveyApi, surveyId, capabilitiesValues);
    yield put(actions.answerSurveySuccess());
    yield put(push(`/he-survey/${surveyId}/completed`));
  } catch (err) {
    yield put(actions.answerSurveyFailure(err.message || String(err)));
    yield put(error(`Failed to submit response: ${err.message || err}`));
  }
}

function* removeSurveyProcess(action) {
  try {
    const { projectId, surveyId } = action.payload;
    yield call(removeSurveyApi, surveyId);
    yield put(actions.removeSurveySuccess());
    yield put(actions.getSurveys(projectId));
    yield put(success('Survey removed'));
  } catch (err) {
    yield put(actions.removeSurveyFailure(err.message || String(err)));
    yield put(error(`Failed to remove survey: ${err.message || err}`));
  }
}

export default function* watchHigherEd() {
  yield all([
    takeLatest(types.RUN_ASSESSMENT, runAssessmentProcess),
    takeLatest(types.GET_ASSESSMENTS, getAssessmentsProcess),
    takeLatest(types.GET_TOP_FEATURES, getTopFeaturesProcess),
    takeLatest(types.HYDRATE_DRAFT, hydrateDraftProcess),
    takeLatest(types.SET_DRAFT, setDraftProcess),
    takeLatest(types.GET_SURVEYS, getSurveysProcess),
    takeLatest(types.CREATE_SURVEY, createSurveyProcess),
    takeLatest(types.GET_SURVEY_DETAILS, getSurveyDetailsProcess),
    takeLatest(types.ANSWER_SURVEY, answerSurveyProcess),
    takeLatest(types.REMOVE_SURVEY, removeSurveyProcess),
  ]);
}
